// src/utils/quote/draftMerge.js
// Merge an anonymous/local wizard draft onto an EXISTING server draft row
// without destroying server-only data.
//
// Used by RequestForm's resume detector (fb#170): when a logged-in user arrives
// on /tilaa with BOTH a server draft and a fresh local draft, the old code
// delete-and-claimed — which wiped the server draft's address, attachments and
// full concrete spec when the local draft was only a partial hand-off (the
// /laskuri "Käytä tarjouksessa" button writeDraft's just maaraM3 + kesto +
// betoniLaatuSelvitys.floorPlan). Merging instead preserves the server draft
// (and its attachments, which live under the server draft id, not in the row).

/**
 * @param {object|null} serverRow  the authoritative server draft row (has osoite,
 *   spec fields, pumppuRequestId; attachments are associated by id, not in the row)
 * @param {object|null} localData  the local draft from localStorage (local wins per field)
 * @param {string[]} patchableKeys keys whose local values should be PATCHed onto
 *   the server draft (the backend re-whitelists the wire fields; non-wire keys are
 *   dropped server-side, so passing the full wizard field list is safe)
 * @returns {{ merged: object, patchFields: object }}
 *   merged      — the row to hydrate FE state from (server pumppuRequestId preserved)
 *   patchFields — the subset of patchableKeys the local draft actually carries
 */
export function mergeLocalIntoServerRow(serverRow, localData, patchableKeys = []) {
    const server = serverRow || {};
    const local = localData || {};

    // Deep-merge betoniLaatuSelvitys so a laskuri floorPlan is ADDED without
    // dropping the server's specMode / guided trail (shallow spread would replace
    // the whole sub-object). null when neither side has one.
    const selvitys =
        server.betoniLaatuSelvitys || local.betoniLaatuSelvitys
            ? { ...(server.betoniLaatuSelvitys || {}), ...(local.betoniLaatuSelvitys || {}) }
            : null;

    const merged = {
        ...server,
        ...local,
        // Keep the server draft's identity — we are updating it in place, not
        // creating a new one — regardless of anything stale on the local draft.
        pumppuRequestId: server.pumppuRequestId,
        betoniLaatuSelvitys: selvitys,
    };

    const patchFields = {};
    for (const k of patchableKeys) {
        if (k in local) patchFields[k] = local[k];
    }

    return { merged, patchFields };
}
